// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Group, Loader, Pagination, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import { isCheckInAppointment } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { TableAnchor } from '../layout/TableAnchor';

const ITEMS_PER_PAGE = 10;
const OVERDUE_DAYS_THRESHOLD = 7;

interface IncompleteNotesTableProps {
  /** All enriched provider appointment details (filtering happens here). */
  appointments: ProviderAppointmentDetails[];
  currentProviderId?: string;
  loading: boolean;
  searchQuery: string;
  showProvider?: boolean;
}

export function getIncompleteNoteDetails(
  appointments: ProviderAppointmentDetails[],
  currentProviderId?: string
): ProviderAppointmentDetails[] {
  const providerScoped = currentProviderId
    ? appointments.filter((details) => details.providerId === currentProviderId)
    : appointments;

  return providerScoped.filter((details) => Boolean(details.completedAt) && !details.hasNote);
}

/**
 * Lists fulfilled appointments that don't yet have a signed clinical note.
 * Reuses the same enriched dataset as TodaysAppointmentsTable.
 */
export function IncompleteNotesTable({
  appointments,
  currentProviderId,
  loading,
  searchQuery,
  showProvider = false,
}: IncompleteNotesTableProps): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const incomplete = useMemo(
    () => getIncompleteNoteDetails(appointments, currentProviderId),
    [appointments, currentProviderId]
  );

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) {
      return incomplete;
    }
    const q = searchQuery.toLowerCase();
    return incomplete.filter(
      (d) =>
        d.patientName.toLowerCase().includes(q) ||
        d.providerName.toLowerCase().includes(q) ||
        d.appointmentType.toLowerCase().includes(q)
    );
  }, [incomplete, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (incomplete.length === 0) {
    return (
      <Text c="dimmed" py="md">
        No incomplete notes.
      </Text>
    );
  }

  return (
    <>
      <Table miw={1000}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Time</Table.Th>
            <Table.Th>Patient</Table.Th>
            {showProvider && <Table.Th>Provider</Table.Th>}
            <Table.Th>Appointment Type</Table.Th>
            <Table.Th>Documents</Table.Th>
            <Table.Th>Time Overdue</Table.Th>
            <Table.Th>Comment</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {paginated.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                />
              </Table.Td>
              <Table.Td>
                <TableAnchor
                  size="sm"
                  onClick={() => {
                    if (details.patientId) {
                      navigate(`/Patient/${details.patientId}`)?.catch(console.error);
                    }
                  }}
                >
                  {details.patientName}
                </TableAnchor>
              </Table.Td>
              {showProvider && <Table.Td>{details.providerName}</Table.Td>}
              <Table.Td>{details.appointmentType}</Table.Td>
              <Table.Td>
                {details.documents.length > 0 ? (
                  <Group gap="xs">
                    {details.documents.map((doc) => (
                      <TableAnchor key={doc.id} size="sm">
                        {doc.title}
                      </TableAnchor>
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    N/A
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <OverdueBadge days={details.timeOverdueDays} />
              </Table.Td>
              <Table.Td>
                <Text size="sm" c={details.comment ? undefined : 'dimmed'}>
                  {details.comment ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <TableAnchor
                  size="sm"
                  onClick={() => {
                    if (details.patientId && details.encounter?.id) {
                      navigate(`/Patient/${details.patientId}/Encounter/${details.encounter.id}`)?.catch(console.error);
                    }
                  }}
                >
                  {isCheckInAppointment(details.appointment) ? 'Coordinator Note' : 'Complete Note'}
                </TableAnchor>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {filtered.length} of {incomplete.length}
          </Text>
          <Pagination value={currentPage} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}
    </>
  );
}

function OverdueBadge({ days }: { days: number | undefined }): JSX.Element {
  if (days === undefined) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  const label = days <= 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`;
  if (days >= OVERDUE_DAYS_THRESHOLD) {
    return (
      <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
        {label}
      </Badge>
    );
  }
  return <Text size="sm">{label}</Text>;
}
